import React, {createRef} from 'react'
import AssistantTimelineEvent from './AssistantTimelineEvent'
import {Box, Stack, styled} from '@mui/material'
import Loading from 'web-common/components/loading/Loading'
import {
  TimelineActionManager,
  TimelineEventData,
  TimelineEventProducer
} from '@fnd/timeline'
import AssistantTimelineMessageCreator from './AssistantTimelineMessageCreator'
import {
  AssistantTimelineViewConfig,
  AssistantTimelineViewConfigDefault
} from './AssistantTimelineViewConfig'

const Wrapper = styled(Stack)(({theme}) => ({
  height: 'calc(var(--app-height) - 130px)',
  width: '100%',
  paddingTop: 0,

  [theme.breakpoints.up('md')]: {
    height: 'calc(var(--app-height) - 98px)',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 30,
    boxShadow: theme.shadows[8],
    marginTop: 15
  }
}))

const ScrollArea = styled(Stack)({
  display: 'flex',
  flexGrow: 1,
  overflowX: 'hidden',
  overflowY: 'auto',

  '& > p:first-of-type': {
    marginTop: '1rem'
  },

  paddingBottom: '1rem'
})

const hiddenScrollStyles = {
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none'
  }
}

const customScrollStyles = {
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    width: '10px'
  },
  '&::-webkit-scrollbar-track': {
    borderRadius: '20px',
    background: 'rgba(0, 0, 0, 0.3)'
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: '20px',
    background: 'rgba(0, 0, 0, 0.6)'
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(0, 0, 0, 0.8)'
  }
}

interface AssistantTimelineViewProps {
  actionManager: TimelineActionManager
  producer: TimelineEventProducer
  config?: Partial<AssistantTimelineViewConfig>
  hideMessageCreator?: boolean
  scrollBarStyle?: 'native' | 'hidden' | 'custom'
  initMessage?: string
}

interface AssistantTimelineViewState {
  events: TimelineEventData<any>[]
  loading: boolean
}

class AssistantTimelineView extends React.Component<
  AssistantTimelineViewProps,
  AssistantTimelineViewState
> {
  state: AssistantTimelineViewState = {
    events: [],
    loading: true
  }

  timelineRef = createRef<HTMLDivElement>()

  readonly config: AssistantTimelineViewConfig = {
    ...AssistantTimelineViewConfigDefault,
    ...this.props.config
  }

  setAppHeight = () => {
    const appHeight = window.innerHeight
    document.documentElement.style.setProperty('--app-height', `${appHeight}px`)
  }

  componentDidMount() {
    this.setAppHeight()
    window.addEventListener('resize', this.setAppHeight)

    this.props.producer.subscribe((data) => {
      this.setState(
        {loading: false, events: data},
        this.onScrollToBottom.bind(this)
      )
    })
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setAppHeight)
    this.props.producer.complete()
    this.props.actionManager.complete()
  }

  onScrollToBottom() {
    setTimeout(() => {
      const node = this.timelineRef.current
      if (node) {
        node.scroll({
          top: node.scrollHeight,
          behavior: 'smooth'
        })
      }
    })
  }

  differentDates(d1: Date, d2: Date) {
    return (
      d1.getFullYear() !== d2.getFullYear() ||
      d1.getMonth() !== d2.getMonth() ||
      d1.getDate() !== d2.getDate()
    )
  }

  shouldShowDate(index: number) {
    if (
      index === 0 ||
      !this.state.events[index] ||
      !this.state.events[index - 1]
    ) {
      return true
    }
    const current = new Date(this.state.events[index]?.date ?? 0)
    const previous = new Date(this.state.events[index - 1]?.date ?? 0)
    return this.differentDates(current, previous)
  }

  renderEvent(event: TimelineEventData<any>, index: number) {
    return (
      <Box key={event._id + index} display="flex" padding={2}>
        <React.Fragment>
          <AssistantTimelineEvent event={event} />
        </React.Fragment>
      </Box>
    )
  }

  renderScrollArea() {
    this.onScrollToBottom()
    let scrollBarStyles = undefined

    if (!this.props.scrollBarStyle || this.props.scrollBarStyle === 'hidden') {
      scrollBarStyles = hiddenScrollStyles
    } else if (this.props.scrollBarStyle === 'custom') {
      scrollBarStyles = customScrollStyles
    }

    return (
      <ScrollArea
        flexDirection="column"
        justifyContent="flex-start"
        spacing={3}
        ref={this.timelineRef}
        sx={scrollBarStyles}
      >
        {this.state.events.map(this.renderEvent.bind(this))}
      </ScrollArea>
    )
  }

  render() {
    const lastEvent = this.state.events[this.state.events.length - 1]
    return (
      <Wrapper flexDirection={'column'} alignItems={'stretch'}>
        {/* Accessibility */}
        <Box className={'sr-only'} aria-live={'polite'} role={'alert'}>
          {lastEvent && this.renderEvent(lastEvent, Date.now())}
        </Box>
        {/* Timeline scroll */}
        <Loading
          loading={this.state.loading}
          render={this.renderScrollArea.bind(this)}
        />
        {/* Chat input */}
        {!this.props.hideMessageCreator && (
          <AssistantTimelineMessageCreator
            producer={this.props.producer}
            actionManager={this.props.actionManager}
            config={this.config}
            initMessage={this.props.initMessage}
          />
        )}
      </Wrapper>
    )
  }
}

export default AssistantTimelineView
